import { fetchJSON } from '../util/fetch_json'
import { humanizeDuration } from '../util/humanize_duration'
import { SharedTimeout } from '../util/shared_timeout'
import { Heartbeat, IHeartbeatData } from './heartbeat'

interface IOptions {
  debug?: boolean
  extension?: boolean
  timeout_advance_warning?: number
  timeout_grace_period?: number
  update_interval?: number
}

class SessionManager {
  private isPromptShown = false
  private options: Readonly<IOptions>
  private sharedTimeout: SharedTimeout

  constructor({
    debug = false,
    extension = false,
    timeout_advance_warning = 60 * 60 * 48, // 48 hours
    timeout_grace_period = 60 * 2, // two minutes
    update_interval = 60, // one minute
  }: IOptions) {
    this.options = Object.freeze({
      debug,
      extension,
      timeout_advance_warning,
      timeout_grace_period,
      update_interval,
    })

    new Heartbeat({
      debug,
      interval: this.options.update_interval,
      onBeat: this.onBeat.bind(this),
    })
  }

  private fetch(url: string, method: string) {
    this.log('fetch')

    return fetchJSON(url, method)
      .then(() => {
        if (this.sharedTimeout) {
          this.sharedTimeout.destroy()
        }
      })
      .catch((error: Error) => {
        window.console.error(error)
        window.location.reload()
      })
  }

  private destroySession() {
    this.log('destroySession')

    this.fetch('/login', 'DELETE').then(() => {
      window.location.reload()
    })
  }

  private extendSession() {
    this.log('extendSession')

    this.fetch('/login/extend', 'POST').then(() => {
      this.dismissAdvanceWarning()
    })
  }

  private log(message: string) {
    if (!this.options.debug) {
      return
    }

    window.console.debug(`SessionManager: ${message}`)
  }

  private onBeat(data: IHeartbeatData) {
    const now = new Date().getTime()
    const then = new Date(data.expires_at).getTime()
    const timeRemaining = (then - now) / 1000

    this.log(`expires at ${data.expires_at}`)

    if (timeRemaining < this.options.timeout_advance_warning) {
      this.showAdvanceWarning(timeRemaining)
      this.startCountdown(timeRemaining)
    } else {
      this.dismissAdvanceWarning()
    }
  }

  private showAdvanceWarning(timeRemaining: number) {
    this.log('showAdvanceWarning')

    if (this.isPromptShown) {
      return
    }

    const buttons = [
      {
        callback: this.destroySession.bind(this),
        style: 'secondary',
        text: 'Log out',
      },
    ]

    let extensionSuffix = ''
    if (this.options.extension) {
      extensionSuffix = ' unless you extend your session'

      buttons.push({
        callback: this.extendSession.bind(this),
        style: 'primary',
        text: 'Stay logged in',
      })
    } else {
      buttons.push({
        callback: this.dismissAdvanceWarning.bind(this),
        style: 'secondary',
        text: 'Keep using Opal',
      })
    }

    const humanized = humanizeDuration(timeRemaining)
    window.AppBase.execute('alert:show', {
      body_text: `You will automatically be logged out in ${humanized}${extensionSuffix}.`,
      buttons,
      header_text: 'Your session will expire soon',
    })
    this.isPromptShown = true
  }

  private dismissAdvanceWarning() {
    if (window.AppBase.request('alert:is_open')) {
      window.AppBase.execute('shroud:close')
    }

    this.isPromptShown = false
  }

  private startCountdown(timeRemaining: number) {
    if (this.sharedTimeout) {
      return
    }

    this.sharedTimeout = new SharedTimeout({
      debug: this.options.debug,
      events: [],
      grace_period: this.options.timeout_grace_period,
      name: 'destroySession',
      onGracePeriodTimeout: () => window.location.reload(),
      onTimeout: () =>
        this.showAdvanceWarning(this.options.timeout_grace_period),
      timeout_period: timeRemaining - this.options.timeout_grace_period,
    })
  }
}

export { SessionManager }
